import React, { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, Loader2, ChevronRight, ArrowLeft, BookOpen, ImageIcon, Eye } from 'lucide-react';
import { adminListSeries, adminCreateSeries, adminUpdateSeries, adminDeleteSeries, adminListEpisodes, adminCreateEpisode, adminUpdateEpisode, adminDeleteEpisode } from '../../services/cms';
import SeriesForm from './SeriesForm';
import EpisodeForm from './EpisodeForm';
import ImagePanel from './ImagePanel';
import { useToast } from '../../hooks/use-toast';

const StatutBadge = ({ statut }) => {
  const map = { publie: 'bg-green-600', brouillon: 'bg-amber-500', termine: 'bg-blue-600' };
  const label = { publie: 'Publié', brouillon: 'Brouillon', termine: 'Terminée' }[statut] || statut;
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-800 text-white uppercase ${map[statut] || 'bg-muted-foreground'}`}>{label}</span>;
};

const CmsManager = () => {
  const [series, setSeries] = useState(null);
  const [selSerie, setSelSerie] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [selEpisode, setSelEpisode] = useState(null);
  const [showSeriesForm, setShowSeriesForm] = useState(false);
  const [editSerie, setEditSerie] = useState(null);
  const [showEpForm, setShowEpForm] = useState(false);
  const [editEp, setEditEp] = useState(null);
  const [loadingEps, setLoadingEps] = useState(false);
  const { toast } = useToast();

  const loadSeries = async () => { try { setSeries(await adminListSeries()); } catch (e) { setSeries([]); } };
  useEffect(() => { loadSeries(); }, []);

  const loadEpisodes = async (serie) => {
    setLoadingEps(true);
    try { setEpisodes(await adminListEpisodes(serie.id)); } catch (e) { /* ignore */ } finally { setLoadingEps(false); }
  };

  const openSerie = (s) => { setSelSerie(s); setSelEpisode(null); loadEpisodes(s); };

  // ----- Series handlers -----
  const submitSeries = async (data) => {
    if (editSerie) { await adminUpdateSeries(editSerie.id, data); toast({ title: 'Série mise à jour' }); }
    else { await adminCreateSeries(data); toast({ title: 'Série créée' }); }
    setShowSeriesForm(false); setEditSerie(null); await loadSeries();
  };
  const delSerie = async (s) => { if (!window.confirm(`Supprimer « ${s.titre} » et ses épisodes ?`)) return; await adminDeleteSeries(s.id); if (selSerie?.id === s.id) setSelSerie(null); await loadSeries(); toast({ title: 'Série supprimée' }); };

  // ----- Episode handlers -----
  const submitEpisode = async (data) => {
    if (editEp) { await adminUpdateEpisode(editEp.id, data); toast({ title: 'Épisode mis à jour' }); }
    else { await adminCreateEpisode(data); toast({ title: 'Épisode créé' }); }
    setShowEpForm(false); setEditEp(null); await loadEpisodes(selSerie);
  };
  const delEpisode = async (ep) => { if (!window.confirm(`Supprimer l’épisode « ${ep.titre} » ?`)) return; await adminDeleteEpisode(ep.id); if (selEpisode?.id === ep.id) setSelEpisode(null); await loadEpisodes(selSerie); toast({ title: 'Épisode supprimé' }); };

  if (series === null) return <div className="py-16 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  // ---------- VIEW: image management for an episode ----------
  if (selEpisode) {
    return (
      <div>
        <button onClick={() => setSelEpisode(null)} className="inline-flex items-center gap-2 font-700 mb-5 hover:text-primary transition-colors"><ArrowLeft className="w-4 h-4" /> Épisodes de « {selSerie.titre} »</button>
        <ImagePanel episode={selEpisode} />
      </div>
    );
  }

  // ---------- VIEW: episodes of a series ----------
  if (selSerie) {
    return (
      <div>
        <button onClick={() => setSelSerie(null)} className="inline-flex items-center gap-2 font-700 mb-5 hover:text-primary transition-colors"><ArrowLeft className="w-4 h-4" /> Toutes les séries</button>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <div>
            <h2 className="font-display font-700 text-2xl flex items-center gap-2">{selSerie.titre} <StatutBadge statut={selSerie.statut} /></h2>
            <p className="text-muted-foreground font-600 text-sm">Gérez les épisodes et leurs images.</p>
          </div>
          <button onClick={() => { setEditEp(null); setShowEpForm(true); }} className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-white font-800 text-sm hover:scale-105 transition-transform"><Plus className="w-4 h-4" /> Nouvel épisode</button>
        </div>

        {showEpForm && <div className="mb-5"><EpisodeForm initial={editEp} serieId={selSerie.id} onSubmit={submitEpisode} onCancel={() => { setShowEpForm(false); setEditEp(null); }} /></div>}

        {loadingEps ? <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        : episodes.length === 0 ? <p className="text-muted-foreground font-600 py-8">Aucun épisode. Créez-en un.</p>
        : (
          <div className="space-y-2">
            {episodes.map((ep) => (
              <div key={ep.id} className="flex items-center gap-3 bg-card border border-border rounded-2xl p-3">
                <div className="w-14 h-14 rounded-lg overflow-hidden bg-muted shrink-0">
                  {ep.image_couverture_url ? <img src={ep.image_couverture_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center font-display font-700 text-primary">{ep.numero}</div>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap"><span className="text-xs font-800 text-primary">Ép. {ep.numero}</span><StatutBadge statut={ep.statut} /></div>
                  <p className="font-display font-600 truncate">{ep.titre}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1"><ImageIcon className="w-3 h-3" /> {ep.images_count} image(s)</p>
                </div>
                <button onClick={() => setSelEpisode(ep)} className="flex items-center gap-1 px-3 py-2 rounded-full bg-secondary text-secondary-foreground font-800 text-xs shrink-0"><ImageIcon className="w-3.5 h-3.5" /> Images</button>
                <a href={`/apercu/${ep.slug}`} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-muted flex items-center justify-center hover:bg-secondary/40 shrink-0" title="Aperçu"><Eye className="w-4 h-4" /></a>
                <button onClick={() => { setEditEp(ep); setShowEpForm(true); }} className="w-9 h-9 rounded-full bg-muted flex items-center justify-center hover:bg-secondary/40 shrink-0"><Edit2 className="w-4 h-4" /></button>
                <button onClick={() => delEpisode(ep)} className="w-9 h-9 rounded-full bg-muted flex items-center justify-center hover:bg-destructive hover:text-white transition-colors shrink-0"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ---------- VIEW: series list ----------
  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div><h2 className="font-display font-700 text-2xl">Séries</h2><p className="text-muted-foreground font-600 text-sm">{series.length} série(s)</p></div>
        <button onClick={() => { setEditSerie(null); setShowSeriesForm(true); }} className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-white font-800 text-sm hover:scale-105 transition-transform"><Plus className="w-4 h-4" /> Nouvelle série</button>
      </div>

      {showSeriesForm && <div className="mb-6"><SeriesForm initial={editSerie} onSubmit={submitSeries} onCancel={() => { setShowSeriesForm(false); setEditSerie(null); }} /></div>}

      {series.length === 0 ? <p className="text-muted-foreground font-600 py-8">Aucune série. Créez-en une ou importez un CSV.</p>
      : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {series.map((s) => (
            <div key={s.id} className="bg-card border border-border rounded-2xl overflow-hidden card-hover">
              <div className="relative h-32 bg-muted">
                {s.couverture_url && <img src={s.couverture_url} alt={s.titre} className="w-full h-full object-cover" />}
                <div className="absolute top-2 left-2"><StatutBadge statut={s.statut} /></div>
              </div>
              <div className="p-4">
                <h3 className="font-display font-700 line-clamp-1">{s.titre}</h3>
                <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1 mb-3"><BookOpen className="w-3 h-3" /> {s.episodes_count} épisode(s)</p>
                <div className="flex items-center gap-2">
                  <button onClick={() => openSerie(s)} className="flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-full bg-primary text-white font-800 text-xs">Gérer <ChevronRight className="w-3.5 h-3.5" /></button>
                  <button onClick={() => { setEditSerie(s); setShowSeriesForm(true); }} className="w-9 h-9 rounded-full bg-muted flex items-center justify-center hover:bg-secondary/40"><Edit2 className="w-4 h-4" /></button>
                  <button onClick={() => delSerie(s)} className="w-9 h-9 rounded-full bg-muted flex items-center justify-center hover:bg-destructive hover:text-white transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CmsManager;
